import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type DossierType = 'B2B' | 'B2C';
export type DossierStatutGlobal =
  | 'brouillon'
  | 'en_cours'
  | 'realise'
  | 'facture'
  | 'encaisse'
  | 'solde'
  | 'annule';
export type DossierStatutFacturation = 'non_emise' | 'emise' | 'deposee_chez_client' | 'envoyee';
export type DossierStatutEncaissement = 'non_recu' | 'acompte_recu' | 'partiel' | 'solde';

export interface DossierRow {
  id: string;
  reference: string;
  titre: string;
  type: DossierType;
  sous_segment_id: string | null;
  formation_id: string | null;
  session_id: string | null;
  client_tier_id: string | null;
  formateur_id: string | null;
  formateurs_co_animateurs_ids: string[];
  lieu_fournisseur_id: string | null;
  commercial_id: string | null;
  approbateur_id: string | null;
  date_debut: string | null;
  date_fin: string | null;
  date_facture_emise: string | null;
  date_facture_deposee_client: string | null;
  date_acompte_recu: string | null;
  date_solde_recu: string | null;
  date_cloture_dossier: string | null;
  prix_vente_ht: number;
  tva_taux: number;
  prix_vente_ttc: number;
  acompte_pct: number;
  statut_facturation: DossierStatutFacturation;
  statut_encaissement: DossierStatutEncaissement;
  statut_global: DossierStatutGlobal;
  convention_url: string | null;
  facture_client_url: string | null;
  bon_commande_client_url: string | null;
  numero_facture: string | null;
  created_at: string;
  updated_at: string;
}

export type DossierChargeCategorie =
  | 'honoraires_formateur'
  | 'hotel_lieu'
  | 'traiteur'
  | 'transport'
  | 'supports_impressions'
  | 'commission_commercial'
  | 'marketing_digital'
  | 'autre';

export interface DossierChargeRow {
  id: string;
  dossier_id: string;
  categorie: DossierChargeCategorie;
  libelle: string;
  tier_id: string | null;
  montant: number;
  tva_taux: number;
  est_pct: boolean;
  pct_valeur: number | null;
  ordre: number;
  obligatoire: boolean;
  created_at: string;
}

export type DossierPaiementType =
  | 'encaissement_client_acompte'
  | 'encaissement_client_solde'
  | 'encaissement_client_autre'
  | 'paiement_formateur'
  | 'paiement_lieu'
  | 'paiement_traiteur'
  | 'paiement_autre_charge';
export type DossierPaiementMode = 'virement' | 'cheque' | 'especes' | 'ccp' | 'carte';
export type DossierPaiementStatut = 'a_payer' | 'planifie' | 'paye' | 'en_retard' | 'annule';

export interface DossierPaiementRow {
  id: string;
  dossier_id: string;
  type: DossierPaiementType;
  charge_id: string | null;
  montant: number;
  mode: DossierPaiementMode;
  reference_externe: string | null;
  banque_emettrice: string | null;
  date_paiement: string | null;
  date_echeance: string | null;
  statut: DossierPaiementStatut;
  preuve_url: string | null;
  compte_bancaire_id: string | null;
  observation: string | null;
  created_at: string;
  created_by: string | null;
}

export interface DossierDocumentRow {
  id: string;
  dossier_id: string;
  type: string;
  nom_fichier: string;
  url: string;
  taille_kb: number | null;
  mime_type: string | null;
  uploaded_at: string;
}

export interface DossierKpis {
  nb_en_cours: number;
  ca_a_encaisser: number;
  benefice_30j: number;
  marge_moyenne_pct: number;
}

export interface DossierSimulateur {
  dossier_id: string;
  reference: string;
  titre: string;
  prix_vente_ht: number;
  prix_vente_ttc: number;
  total_charges: number;
  benefice_brut: number;
  marge_pct: number;
  charges_par_categorie: Record<string, number>;
}

const ALL = ['*'].join(',');

// =====================================================================
// Liste + KPIs
// =====================================================================
export function useDossiersKPIs() {
  return useQuery({
    queryKey: ['dossiers_kpis'],
    queryFn: async (): Promise<DossierKpis | null> => {
      const { data, error } = await supabase.from('v_dossiers_kpis').select('*').maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as Record<string, unknown>;
      return {
        nb_en_cours: Number(r.nb_en_cours ?? 0),
        ca_a_encaisser: Number(r.ca_a_encaisser ?? 0),
        benefice_30j: Number(r.benefice_30j ?? 0),
        marge_moyenne_pct: Number(r.marge_moyenne_pct ?? 0),
      };
    },
    staleTime: 60_000,
  });
}

export function useDossiers(filter: {
  type?: DossierType | null;
  statut?: DossierStatutGlobal | null;
  search?: string;
}) {
  return useQuery({
    queryKey: ['dossiers', filter],
    queryFn: async (): Promise<DossierRow[]> => {
      let q = supabase
        .from('dossiers_formation')
        .select(ALL)
        .order('created_at', { ascending: false });
      if (filter.type) q = q.eq('type', filter.type);
      if (filter.statut) q = q.eq('statut_global', filter.statut);
      if (filter.search && filter.search.length >= 2) {
        q = q.or(`titre.ilike.%${filter.search}%,reference.ilike.%${filter.search}%`);
      }
      const { data, error } = await q.limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as DossierRow[];
    },
  });
}

export function useDossier(id: string | null) {
  return useQuery({
    queryKey: ['dossier', id],
    enabled: !!id,
    queryFn: async (): Promise<DossierRow | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('dossiers_formation')
        .select(ALL)
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as DossierRow) ?? null;
    },
  });
}

export function useDossierSimulateur(id: string | null) {
  return useQuery({
    queryKey: ['dossier_simulateur', id],
    enabled: !!id,
    queryFn: async (): Promise<DossierSimulateur | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('v_dossier_simulateur')
        .select('*')
        .eq('dossier_id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as Record<string, unknown>;
      return {
        dossier_id: String(r.dossier_id),
        reference: String(r.reference ?? ''),
        titre: String(r.titre ?? ''),
        prix_vente_ht: Number(r.prix_vente_ht ?? 0),
        prix_vente_ttc: Number(r.prix_vente_ttc ?? 0),
        total_charges: Number(r.total_charges ?? 0),
        benefice_brut: Number(r.benefice_brut ?? 0),
        marge_pct: Number(r.marge_pct ?? 0),
        charges_par_categorie: (r.charges_par_categorie as Record<string, number>) ?? {},
      };
    },
    staleTime: 30_000,
  });
}

export function useDossierCharges(id: string | null) {
  return useQuery({
    queryKey: ['dossier_charges', id],
    enabled: !!id,
    queryFn: async (): Promise<DossierChargeRow[]> => {
      if (!id) return [];
      const { data, error } = await supabase
        .from('dossier_charges')
        .select('*')
        .eq('dossier_id', id)
        .order('ordre');
      if (error) throw error;
      return (data ?? []) as unknown as DossierChargeRow[];
    },
  });
}

export function useDossierPaiements(id: string | null) {
  return useQuery({
    queryKey: ['dossier_paiements', id],
    enabled: !!id,
    queryFn: async (): Promise<DossierPaiementRow[]> => {
      if (!id) return [];
      const { data, error } = await supabase
        .from('dossier_paiements')
        .select('*')
        .eq('dossier_id', id)
        .order('date_paiement', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as DossierPaiementRow[];
    },
  });
}

export function useDossierDocuments(id: string | null) {
  return useQuery({
    queryKey: ['dossier_documents', id],
    enabled: !!id,
    queryFn: async (): Promise<DossierDocumentRow[]> => {
      if (!id) return [];
      const { data, error } = await supabase
        .from('dossier_documents')
        .select('*')
        .eq('dossier_id', id)
        .order('uploaded_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as DossierDocumentRow[];
    },
  });
}

// =====================================================================
// Mutations
// =====================================================================
export function useUpsertDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<DossierRow> & { titre: string; type: DossierType },
    ): Promise<DossierRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('dossiers_formation')
          .update(rest as never)
          .eq('id', id)
          .select(ALL)
          .single();
        if (error) throw error;
        return data as unknown as DossierRow;
      }
      const { data, error } = await supabase
        .from('dossiers_formation')
        .insert(rest as never)
        .select(ALL)
        .single();
      if (error) throw error;
      return data as unknown as DossierRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dossiers'] });
      void qc.invalidateQueries({ queryKey: ['dossier'] });
      void qc.invalidateQueries({ queryKey: ['dossiers_kpis'] });
    },
  });
}

export function useUpsertDossierCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<DossierChargeRow> & {
        dossier_id: string;
        categorie: DossierChargeCategorie;
        libelle: string;
        montant: number;
      },
    ): Promise<DossierChargeRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('dossier_charges')
          .update(rest as never)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as DossierChargeRow;
      }
      const { data, error } = await supabase
        .from('dossier_charges')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as DossierChargeRow;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['dossier_charges', vars.dossier_id] });
      void qc.invalidateQueries({ queryKey: ['dossier_simulateur', vars.dossier_id] });
      void qc.invalidateQueries({ queryKey: ['dossiers_kpis'] });
    },
  });
}

export function useDeleteDossierCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; dossier_id: string }): Promise<void> => {
      const { error } = await supabase.from('dossier_charges').delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['dossier_charges', vars.dossier_id] });
      void qc.invalidateQueries({ queryKey: ['dossier_simulateur', vars.dossier_id] });
    },
  });
}

export function useAddDossierPaiement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<DossierPaiementRow> & {
        dossier_id: string;
        type: DossierPaiementType;
        montant: number;
      },
    ): Promise<DossierPaiementRow> => {
      const { data, error } = await supabase
        .from('dossier_paiements')
        .insert(input as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as DossierPaiementRow;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['dossier_paiements', vars.dossier_id] });
      void qc.invalidateQueries({ queryKey: ['dossiers_kpis'] });
    },
  });
}
